import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, discount, setDiscount, discountAmount, total } = useCart();
  const [code, setCode] = useState(discount?.code || "");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const applyDiscount = async () => {
    if (!code.trim()) return;
    try {
      const r = await api.post("/discount/validate", { code });
      setDiscount(r.data);
      toast.success(`Code applied — ${r.data.percent_off}% off`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
      setDiscount(null);
    }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
        })),
        origin_url: window.location.origin,
        discount_code: discount?.code || null,
        email: email || null,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24 mx-auto max-w-[1000px] px-6 text-center" data-testid="cart-empty">
        <div className="overline text-muted-foreground">Bag</div>
        <h1 className="serif text-5xl mt-4">Your bag is empty.</h1>
        <p className="text-muted-foreground mt-4">A few pieces are waiting.</p>
        <Link to="/shop" className="btn-sharp primary mt-10 inline-block">Shop the Edit</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 mx-auto max-w-[1400px] px-6 md:px-12" data-testid="cart-page">
      <div className="overline text-muted-foreground">Your Bag</div>
      <h1 className="serif text-5xl md:text-6xl mt-3">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mt-12">
        <div className="lg:col-span-2 divide-y divide-white/10 border-y border-white/10">
          {items.map((it, idx) => (
            <div key={idx} className="py-8 grid grid-cols-[100px_1fr_auto] md:grid-cols-[140px_1fr_auto] gap-6 items-start" data-testid={`cart-item-${idx}`}>
              <div className="aspect-[3/4] overflow-hidden bg-muted/40">
                {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0">
                <h3 className="serif text-2xl font-light">{it.name}</h3>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-2">
                  {it.size && <span>Size {it.size}</span>}
                  {it.size && it.color && <span className="mx-2">•</span>}
                  {it.color && <span>{it.color}</span>}
                </div>
                <div className="mt-5 flex items-center gap-4">
                  <div className="flex items-center border border-white/20">
                    <button onClick={() => updateQty(idx, -1)} className="px-3 py-2" data-testid={`item-${idx}-minus`}><Minus size={13} /></button>
                    <span className="px-3 text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(idx, 1)} className="px-3 py-2" data-testid={`item-${idx}-plus`}><Plus size={13} /></button>
                  </div>
                  <button onClick={() => removeItem(idx)} className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`item-${idx}-remove`}>
                    <X size={13} /> Remove
                  </button>
                </div>
              </div>
              <div className="text-right text-sm font-light whitespace-nowrap">
                ${(it.price * it.quantity).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:sticky lg:top-28 self-start">
          <div className="overline text-muted-foreground mb-6">Summary</div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            {discount && (
              <div className="flex justify-between"><span className="text-muted-foreground">Discount ({discount.code})</span><span data-testid="cart-discount">− ${discountAmount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>Complimentary</span></div>
            <div className="border-t border-white/10 pt-4 flex justify-between text-lg serif">
              <span>Total</span>
              <span data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-8">
            <div className="overline text-muted-foreground mb-2">Promo Code</div>
            <div className="flex gap-2">
              <input
                className="input-sharp"
                placeholder="ENTER CODE"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                data-testid="discount-code-input"
              />
              <button className="btn-sharp ghost whitespace-nowrap" onClick={applyDiscount} data-testid="apply-discount-btn">Apply</button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 uppercase tracking-[0.18em]">Try WELCOME10 or LUXE25</div>
          </div>

          <div className="mt-8">
            <div className="overline text-muted-foreground mb-2">Email (for receipt)</div>
            <input
              className="input-sharp"
              type="email"
              placeholder="you@domain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="checkout-email-input"
            />
          </div>

          <button className="btn-sharp primary w-full mt-8" onClick={checkout} disabled={loading} data-testid="checkout-btn">
            {loading ? "Redirecting…" : "Secure Checkout"}
          </button>
          <p className="text-[11px] text-muted-foreground uppercase tracking-[0.18em] mt-4 text-center">
            Payments secured by Stripe
          </p>
        </aside>
      </div>
    </div>
  );
}
